import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Sparkles, Zap } from 'lucide-react';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CreditGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditsRemaining: number;
  creditsRequired: number;
  actionLabel: string;
  onGetCredits: () => void;
}

const CreditGateModal: React.FC<CreditGateModalProps> = ({
  isOpen,
  onClose,
  creditsRemaining,
  creditsRequired,
  actionLabel,
  onGetCredits,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 99999 }}
        >
          {/* Backdrop — covers everything including top bar */}
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-md"
            style={{ zIndex: 99999 }}
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden"
            style={{
              zIndex: 100000,
              background: 'var(--ds-bg-card, #ffffff)',
              border: '1px solid var(--ds-border, #e2e8f0)',
              boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />

            <div className="p-6 sm:p-8 text-center">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-amber-500/10">
                <Lock className="w-8 h-8 text-amber-500" />
              </div>

              {/* Title */}
              <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-white">
                {creditsRemaining <= 0 ? 'Credits Exhausted' : 'Not Enough Credits'}
              </h2>

              {/* Subtitle */}
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-5 leading-relaxed">
                <strong className="text-gray-900 dark:text-white">{actionLabel}</strong> requires{' '}
                <span className="font-bold text-amber-600 dark:text-amber-400">{creditsRequired} credits</span>.
                {creditsRemaining > 0
                  ? <> You have <span className="font-bold">{creditsRemaining}</span> remaining.</>
                  : <> You have no credits left.</>}
              </p>

              {/* Plan card */}
              <div
                className="rounded-xl p-4 mb-5 text-left"
                style={{
                  background: 'var(--ds-bg-elevated, #f8fafc)',
                  border: '1px solid var(--ds-border, #e2e8f0)',
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">Unlimited Plan — ₹349/mo</span>
                </div>
                <ul className="space-y-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <li className="flex items-center gap-2">
                    <Zap className="w-3 h-3 text-green-500 shrink-0" />
                    Unlimited resume optimizations
                  </li>
                  <li className="flex items-center gap-2">
                    <Zap className="w-3 h-3 text-green-500 shrink-0" />
                    Unlimited cover & application letters
                  </li>
                  <li className="flex items-center gap-2">
                    <Zap className="w-3 h-3 text-green-500 shrink-0" />
                    Application packs for every matched job — you review and submit
                  </li>
                  <li className="flex items-center gap-2">
                    <Zap className="w-3 h-3 text-green-500 shrink-0" />
                    Cancel anytime — no lock-in
                  </li>
                </ul>
              </div>

              {/* CTA buttons */}
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={onGetCredits}
                  className="w-full min-h-[48px] rounded-xl font-bold text-sm sm:text-base text-white transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #5F8568, #4a7a54)' }}
                >
                  Unlock Unlimited Access →
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full min-h-[44px] rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default React.memo(CreditGateModal);
